import React, { useMemo, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./homeSections.css";
import { notificationsData } from "../../data/home";
import assetUrl from "../../services/assetUrl";

const toLabel = (key) => key.charAt(0) + key.slice(1).toLowerCase();

const categoryIcons = {
  GENERAL: (
    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  EXAM: (
    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  PLACEMENTS: (
    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  EVENTS: (
    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  )
};

const LatestNotifications = ({ data }) => {
  const { eyebrow, heading, categories: rawCategories } = data || notificationsData;

  // Process categories: GENERAL aggregates notifications from all categories (Exam, Placements, Events, etc.)
  const categories = useMemo(() => {
    const raw = rawCategories || {};
    const specificCategories = Object.entries(raw).filter(([key]) => key.toUpperCase() !== "GENERAL");
    
    // Explicit general items if any
    const explicitGeneral = (raw.GENERAL || []).map((item) => ({
      ...item,
      categoryTag: item.categoryTag || "General",
    }));

    // Gather specific items with category tags
    const specificItems = [];
    specificCategories.forEach(([catKey, catItems]) => {
      (catItems || []).forEach((item) => {
        specificItems.push({
          ...item,
          categoryTag: item.categoryTag || toLabel(catKey),
        });
      });
    });

    // Combined all items for GENERAL tab (no duplicate titles)
    const seenTitles = new Set();
    const allGeneralItems = [];
    [...explicitGeneral, ...specificItems].forEach((item) => {
      if (item && item.title && !seenTitles.has(item.title.trim().toLowerCase())) {
        seenTitles.add(item.title.trim().toLowerCase());
        allGeneralItems.push(item);
      }
    });

    return {
      GENERAL: allGeneralItems,
      ...Object.fromEntries(
        specificCategories.map(([k, v]) => [k, (v || []).map((item) => ({ ...item, categoryTag: toLabel(k) }))])
      ),
    };
  }, [rawCategories]);

  const tabs = Object.keys(categories);
  const [activeTab, setActiveTab] = useState(tabs[0] || "GENERAL");
  const [activeIndex, setActiveIndex] = useState(0);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const items = categories[activeTab] || [];

  return (
    <section className="notif-section grain-carousel-bg" aria-labelledby="notif-title">
      {/* Noise Grain SVG Filter & Mesh Background Highlights */}
      <div className="grain-overlay" aria-hidden="true">
        <svg className="grain-svg">
          <filter id="grain-noise">
            <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
          </filter>
          <rect width="100%" height="100%" filter="url(#grain-noise)" opacity="0.06" />
        </svg>
      </div>

      <div className="grain-glow-orb orb-1" aria-hidden="true" />
      <div className="grain-glow-orb orb-2" aria-hidden="true" />

      <div className="container relative-content">
        <div className="notif-header-flex">
          <div>
            {eyebrow && <span className="grain-eyebrow"><span className="pulse-dot" />{eyebrow}</span>}
            <h2 id="notif-title" className="grain-notif-title">{heading}</h2>
          </div>
          
          <div className="grain-tab-pills" role="tablist" aria-label="Notification categories">
            {tabs.map((cat) => (
              <button
                key={cat}
                type="button"
                role="tab"
                aria-selected={cat === activeTab}
                className={`grain-tab-pill${cat === activeTab ? " is-active" : ""}`}
                onClick={() => {
                  setActiveTab(cat);
                  setActiveIndex(0);
                }}
              >
                <span className="pill-icon">{categoryIcons[cat] || categoryIcons.GENERAL}</span>
                <span className="pill-text">{toLabel(cat)}</span>
                <span className="pill-badge">{categories[cat]?.length || 0}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grain-carousel-wrapper">
          <Swiper
            key={activeTab}
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={22}
            slidesPerView={1}
            autoplay={{ delay: 4500, disableOnInteraction: false }}
            navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
            onBeforeInit={(swiper) => {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }}
            onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
            pagination={{ el: ".grain-notif-dots", clickable: true }}
            breakpoints={{
              640: { slidesPerView: 1.2 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
              1280: { slidesPerView: 4 },
            }}
            className="grain-swiper"
          >
            {items.map((notif, i) => (
              <SwiperSlide key={i} className="grain-slide">
                <a
                  className="grain-card"
                  href={assetUrl(notif.pdf)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="grain-card__top">
                    <div className="grain-card__badge">
                      <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span>{notif.categoryTag ? `${notif.categoryTag.toUpperCase()} NOTICE` : "OFFICIAL NOTICE"}</span>
                    </div>
                    <div className="grain-card__top-right">
                      <span className="grain-card__index">#{String(i + 1).padStart(2, '0')}</span>
                      <svg className="grain-card__external-icon" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </div>
                  </div>

                  <h3 className="grain-card__title" title={notif.title}>{notif.title}</h3>

                  <div className="grain-card__click-hint">
                    <span>Click to view notice</span>
                    <svg className="hint-arrow" width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </div>
                </a>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Grain Carousel Bottom Bar Controls */}
          <div className="grain-controls-bar">
            <div className="grain-counter">
              <span className="current">{String(activeIndex + 1).padStart(2, '0')}</span>
              <span className="divider">/</span>
              <span className="total">{String(items.length).padStart(2, '0')}</span>
            </div>

            <div className="grain-notif-dots" />

            <div className="grain-nav-btns">
              <button ref={prevRef} type="button" className="grain-nav-btn" aria-label="Previous slide">
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
              </button>
              <button ref={nextRef} type="button" className="grain-nav-btn" aria-label="Next slide">
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LatestNotifications;

